import { createTheme, Theme } from "@mui/material/styles";
import { TypographyOptions } from "@mui/material/styles/createTypography";

// Main colors
const primaryColor = "#2563eb"; // Vibrant blue
const secondaryColor = "#8b5cf6"; // Purple
const infoColor = "#0ea5e9"; // Light blue
const successColor = "#10b981"; // Green
const warningColor = "#f59e0b"; // Amber
const errorColor = "#ef4444"; // Red
const darkColor = "#18181b"; // Almost black

// Creating the theme
const theme: Theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: primaryColor,
      dark: "#1e40af",
      light: "#60a5fa",
      contrastText: "#ffffff",
    },
    secondary: {
      main: secondaryColor,
      dark: "#6d28d9",
      light: "#a78bfa",
      contrastText: "#ffffff",
    },
    info: {
      main: infoColor,
      dark: "#0284c7",
      light: "#38bdf8",
    },
    success: {
      main: successColor,
      dark: "#059669",
      light: "#34d399",
    },
    warning: {
      main: warningColor,
      dark: "#d97706",
      light: "#fbbf24",
    },
    error: {
      main: errorColor,
      dark: "#dc2626",
      light: "#f87171",
    },
    background: {
      default: "#f5f7fa",
      paper: "#ffffff",
    },
    text: {
      primary: darkColor,
      secondary: "#4b5563",
      disabled: "#9ca3af",
    },
    divider: "#e2e8f0",
  },
  typography: {
    fontFamily: [
      "Inter",
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
    ].join(","),
    h1: {
      fontWeight: 700,
      fontSize: "2.5rem",
    },
    h2: {
      fontWeight: 700,
      fontSize: "2rem",
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.75rem",
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.5rem",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.25rem",
    },
    h6: {
      fontWeight: 600,
      fontSize: "1rem",
    },
    subtitle1: {
      fontSize: "1rem",
      fontWeight: 500,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      textTransform: "none",
    },
  } as TypographyOptions,
  shape: {
    borderRadius: 10,
  },
  shadows: [
    "none",
    "0px 1px 3px rgba(0,0,0,0.04), 0px 1px 2px rgba(0,0,0,0.06)",
    "0px 1px 5px rgba(0,0,0,0.05), 0px 1px 3px rgba(0,0,0,0.08)",
    "0px 2px 4px rgba(0,0,0,0.06), 0px 1px 4px rgba(0,0,0,0.1)",
    "none", // Adding an extra shadow to make it 25 elements as required by the type
    "0px 4px 8px rgba(0,0,0,0.07), 0px 2px 6px rgba(0,0,0,0.12)",
    "0px 6px 12px rgba(0,0,0,0.08), 0px 3px 8px rgba(0,0,0,0.14)",
    "0px 6px 14px rgba(0,0,0,0.1), 0px 3px 10px rgba(0,0,0,0.16)",
    "0px 7px 16px rgba(0,0,0,0.12), 0px 4px 12px rgba(0,0,0,0.17)",
    "0px 8px 18px rgba(0,0,0,0.13), 0px 5px 14px rgba(0,0,0,0.18)",
    "0px 9px 20px rgba(0,0,0,0.14), 0px 6px 16px rgba(0,0,0,0.19)",
    "0px 10px 22px rgba(0,0,0,0.15), 0px 7px 18px rgba(0,0,0,0.2)",
    "0px 11px 24px rgba(0,0,0,0.16), 0px 8px 20px rgba(0,0,0,0.21)",
    "0px 12px 26px rgba(0,0,0,0.17), 0px 9px 22px rgba(0,0,0,0.22)",
    "0px 13px 28px rgba(0,0,0,0.18), 0px 10px 24px rgba(0,0,0,0.23)",
    "0px 14px 30px rgba(0,0,0,0.19), 0px 11px 26px rgba(0,0,0,0.24)",
    "0px 15px 32px rgba(0,0,0,0.2), 0px 12px 28px rgba(0,0,0,0.25)",
    "0px 16px 34px rgba(0,0,0,0.21), 0px 13px 30px rgba(0,0,0,0.26)",
    "0px 17px 36px rgba(0,0,0,0.22), 0px 14px 32px rgba(0,0,0,0.27)",
    "0px 18px 38px rgba(0,0,0,0.23), 0px 15px 34px rgba(0,0,0,0.28)",
    "0px 19px 40px rgba(0,0,0,0.24), 0px 16px 36px rgba(0,0,0,0.29)",
    "0px 20px 42px rgba(0,0,0,0.25), 0px 17px 38px rgba(0,0,0,0.3)",
    "0px 21px 44px rgba(0,0,0,0.26), 0px 18px 40px rgba(0,0,0,0.31)",
    "0px 22px 46px rgba(0,0,0,0.27), 0px 19px 42px rgba(0,0,0,0.32)",
    "0px 23px 48px rgba(0,0,0,0.28), 0px 20px 44px rgba(0,0,0,0.33)",
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "10px 16px",
          boxShadow:
            "0px 1px 3px rgba(0,0,0,0.04), 0px 1px 2px rgba(0,0,0,0.06)",
          "&:hover": {
            boxShadow:
              "0px 4px 8px rgba(0,0,0,0.07), 0px 2px 6px rgba(0,0,0,0.12)",
          },
        },
        contained: {
          "&:hover": {
            transform: "translateY(-1px)",
          },
        },
        containedPrimary: {
          background: `linear-gradient(145deg, ${primaryColor} 0%, #1d4ed8 100%)`,
        },
        containedSecondary: {
          background: `linear-gradient(145deg, ${secondaryColor} 0%, #7c3aed 100%)`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow:
            "0px 1px 3px rgba(0,0,0,0.04), 0px 1px 2px rgba(0,0,0,0.06)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow:
            "0px 1px 3px rgba(0,0,0,0.04), 0px 1px 2px rgba(0,0,0,0.06)",
        },
        elevation2: {
          boxShadow:
            "0px 1px 5px rgba(0,0,0,0.05), 0px 1px 3px rgba(0,0,0,0.08)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow:
            "0px 4px 8px rgba(0,0,0,0.07), 0px 2px 6px rgba(0,0,0,0.05)",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow:
              "0px 8px 16px rgba(0,0,0,0.1), 0px 4px 8px rgba(0,0,0,0.08)",
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 8,
        },
        track: {
          borderRadius: 22 / 2,
        },
        thumb: {
          boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow:
            "0px 4px 8px rgba(0,0,0,0.07), 0px 2px 6px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: "#f8fafc",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 24,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "16px 24px",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 6,
          backgroundColor: darkColor,
          padding: "8px 12px",
          fontSize: "0.75rem",
        },
      },
    },
  },
});

export default theme;
